import { Navigate, Route, Routes, useOutletContext, useParams } from "react-router-dom";
import type { QueryClient } from "@tanstack/react-query";

import { Shell } from "./app/Shell";
import { JobShell } from "./app/JobShell";
import { RequireAuth, SessionProvider } from "./app/SessionGate";
import { LoginScreen } from "./screens/Login";
import { JobsScreen } from "./screens/Jobs";
import { ReviewScreen } from "./screens/Review";
import { ExportScreen } from "./screens/Export";
import { HistoryScreen } from "./screens/History";
import { AccountScreen } from "./screens/Account";
import { AccountJobsScreen } from "./screens/AccountJobs";
import { PolicyNoticeScreen } from "./screens/PolicyNotice";
import { SettingsScreen } from "./screens/Settings";
import { ServiceIntroScreen } from "./screens/public/ServiceIntro";
import { RefundPolicyScreen } from "./screens/public/RefundPolicy";
import { PrivacyScreen } from "./screens/public/Privacy";
import { TermsScreen } from "./screens/public/Terms";
import { PRIVACY_NEXT_PATH, PUBLIC_PATHS } from "./screens/public/paths";
import { BusinessInfo } from "./components/BusinessInfo";
import { UploadingStage } from "./screens/stages/Uploading";
import { PayStage } from "./screens/stages/Pay";
import { MakingStage } from "./screens/stages/Making";
import { ResultStage } from "./screens/stages/Result";
import type { ShellJob } from "./lib/shell-job";

/** 주소는 정본 목업의 것을 그대로 쓴다.
 *
 * - 상단 탭이 없어져 `내 작업` 하나가 본문이고 `알림`은 헤더의 벨이 제자리에서 연다.
 * - **작업 하나에 매인 화면은 전부 `/jobs/:jobId` 아래**다. 입구 넷(표의 행 · 이어서 하기
 *   카드 · `A-53` 의 QR · 알림 행)이 모두 그 한 곳으로 떨어지고, **어느 화면이 열릴지는
 *   입구가 아니라 작업의 상태**가 정한다(PRD 7.7.0).
 * - 계정 화면은 `/account` 아래다 — `/account`(`W-70`) · `/account/jobs`(`W-55`) ·
 *   `/account/settings`(`W-80`).
 * - `/notifications` 같은 옛 주소는 남기지 않는다. 알림이 전용 화면이 아니게 됐으므로
 *   그 주소를 살려 두면 없는 화면으로 가는 링크가 생긴다.
 * - **`/login` 과 로그인 전 화면 넷(`P-01`~`P-04`) 밖의 모든 주소가 `RequireAuth` 아래에
 *   있다** (PRD `7.7.1`, 2026-09-09. 로그인 전 화면은 2026-10-03 `7.0` 「로그인 전 화면」).
 *   로그인 전 화면은 결제사 심사가 **로그인 없이** 보는 자리라 게이트 밖에 두고, 셀러 콘솔
 *   셸도 쓰지 않는다. `/j/:jobId` 는 게이트 안이다. QR 로 들어온 사람도 로그인부터 한다.
 * - **사업자 정보는 라우트 바깥에 한 번 둔다.** 모든 웹 화면 맨 아래에 같은 블록이 서야 하고
 *   (PRD `7.0`), 화면마다 그리면 한 군데가 빠진다. 바깥 틀(`.site`)이 화면 높이를 채워, 내용이
 *   짧거나 아직 그려지지 않은 동안에도 블록은 맨 아래에 붙는다. */
export function App({
  sessionClient,
  createDataClient,
}: {
  /** 세션 질문만 담는 고정 클라이언트(`main.tsx`). */
  sessionClient: QueryClient;
  /** 셀러의 데이터를 담는 클라이언트를 만든다. 계정이 바뀔 때마다 새로 만든다. */
  createDataClient: () => QueryClient;
}) {
  return (
    <SessionProvider sessionClient={sessionClient} createDataClient={createDataClient}>
      <div className="site">
        <div className="site-main">
          <AppRoutes />
        </div>
        <BusinessInfo />
      </div>
    </SessionProvider>
  );
}

function AppRoutes() {
  return (
      <Routes>
        <Route path="/login" element={<LoginScreen />} />

        {/* 로그인 전 화면. **게이트 밖이다.** `/` 는 로그인한 셀러를 내 작업으로 보낸다
            (`ServiceIntroScreen` 머리말). 나머지 셋은 로그인 여부와 상관없이 같은 문서다. */}
        <Route path={PUBLIC_PATHS.service} element={<ServiceIntroScreen />} />
        <Route path={PUBLIC_PATHS.refund} element={<RefundPolicyScreen />} />
        <Route path={PUBLIC_PATHS.terms} element={<TermsScreen />} />
        <Route path={PUBLIC_PATHS.privacy} element={<PrivacyScreen />} />
        {/* `P-04b` 의 `바뀐 뒤 전문 ›`. 시행일 전의 개정 판을 그린다(`PrivacyScreen` 머리말). */}
        <Route path={PRIVACY_NEXT_PATH} element={<PrivacyScreen version="next" />} />

        <Route element={<RequireAuth />}>
          <Route element={<Shell />}>
            <Route path="/jobs" element={<JobsScreen />} />
            <Route path="/account" element={<AccountScreen />} />
            <Route path="/account/jobs" element={<AccountJobsScreen />} />
            <Route path="/account/settings" element={<SettingsScreen />} />
            {/* `W-90` 공개 문서가 바뀔 때 알리는 화면(PRD `7.5.17`). */}
            <Route path="/account/notices/privacy" element={<PolicyNoticeScreen />} />
          </Route>

          <Route path="/jobs/:jobId" element={<JobShell />}>
            {/* 입구가 아니라 **작업의 상태**가 어느 화면을 열지 정한다. */}
            <Route index element={<StageIndex />} />
            <Route path="upload" element={<UploadingStage />} />
            <Route path="pay" element={<PayStage />} />
            <Route path="result" element={<ResultStage />} />
            <Route path="review" element={<ReviewScreen />} />
            <Route path="export" element={<ExportScreen />} />
            <Route path="history" element={<HistoryScreen />} />
          </Route>

          {/* 짧은 경로. QR 에 들어가므로 그대로 유지한다. 도메인만 미정이다. */}
          <Route path="/j/:jobId" element={<HandoffRedirect />} />
        </Route>

        <Route path="*" element={<Navigate to="/jobs" replace />} />
      </Routes>
  );
}

/** `/jobs/:jobId` 로 떨어진 뒤 어느 화면을 세울지 고른다.
 *
 * **입구마다 다른 화면으로 보내지 않는다** — 표의 행 · 이어서 하기 카드 · QR · 알림 행이
 * 전부 여기로 오고, 고르는 근거는 작업의 상태 하나다(PRD 7.7.0). `만드는 중`만 이 자리에서
 * 바로 그린다. 그 화면은 주소를 따로 갖지 않는다. */
function StageIndex() {
  const job = useOutletContext<ShellJob>();
  switch (job.stage) {
    case "UPLOADING":
      return <Navigate to="upload" replace />;
    case "PAY":
      return <Navigate to="pay" replace />;
    case "RESULT":
      return <Navigate to="result" replace />;
    case "REVIEW":
      return <Navigate to="review" replace />;
    case "MAKING":
    default:
      return <MakingStage />;
  }
}

/** `A-53` 의 QR·짧은 URL 은 작업 셸과 **같은 곳으로 떨어진다**. 다른 곳으로 보내면 셀러는
 *  같은 작업을 두 종류로 읽는다(PRD 7.7.0 동선 표). */
function HandoffRedirect() {
  const { jobId } = useParams();
  return <Navigate to={jobId ? `/jobs/${jobId}` : "/jobs"} replace />;
}
